import { PosState } from "@unleashed-admin/stores/PosSlice";
import { PointOfSaleCategory } from "../enums/PointOfSaleCategory";
import { Account } from "../features/account/models/Account";
import { AccountParticipant } from "../features/account/models/AccountParticipant";
import {
  CartItem,
  PrePurchasedItem,
  PurchaseItemViewModel,
  MembershipAllocationDataModel,
} from "../models";

export const isMembership = (
  item: CartItem | PrePurchasedItem | PurchaseItemViewModel
) => {
  return item.type === PointOfSaleCategory.Membership || item.type === PointOfSaleCategory.AnnualPass;
};

export const isMembershipOnlyExcludingAP = (
  item: CartItem | PrePurchasedItem | PurchaseItemViewModel
) => {
  return item.type === PointOfSaleCategory.Membership;
};

export const isAnnualPass = (
  item: CartItem | PrePurchasedItem | PurchaseItemViewModel
) => {
  return item.type === PointOfSaleCategory.AnnualPass;
};

export const accountOwnerAllocatedMembershipIds = (
  accountOwner: AccountParticipant | undefined
) => {
  const membershipIds: number[] = [];

  accountOwner?.waiverParticipants?.forEach((participant: Account) => {
    if (participant.allocatedMembership?.productId) {
      membershipIds.push(participant.allocatedMembership.productId);
    }
  });

  return membershipIds;
};

export const unassignAllParticipantsFromMembership = (
  accountOwner: AccountParticipant | undefined,
  membershipItem: CartItem
) => {
  const membershipIds: number[] = [];

  accountOwner?.waiverParticipants?.forEach((participant: Account) => {
    if (membershipItem &&
      participant.allocatedMembership?.productId == membershipItem.productId
    ) {
      participant.allocatedMembership = undefined;
    }
  });

  return membershipIds;
};

/* Function updates the assigned people for the item in itemsInCart based on membershipAllocationData */
const updatePeopleForItemOnCheckIn = (state: PosState) => {
  state.selectedAccount?.waiverParticipants?.forEach(
    (r) => r.allocatedMembership
  );

  state.itemsInCart = state.itemsInCart?.map((item: CartItem) => {
    if (isMembership(item) && item.productId) {
      const participants =
        state.selectedAccount?.waiverParticipants?.filter(
          (account: Account) =>
            account.allocatedMembership?.productId === item.productId
        ) || [];

      return {
        ...item,
        assignedPeople: participants,
        quantity: participants.length,
      };
    }

    return item;
  });
};

/* Function updates the state after membership check-in */
export const updateStateAfterMembershipCheckIn = (state: PosState) => {
  updatePeopleForItemOnCheckIn(state);
};

export const isParticipantCheckedIn = (
  ticketAllocationData: MembershipAllocationDataModel[],
  participantId?: number
) => {
  return (
    ticketAllocationData.filter(
      (data) => data.participantId === participantId && data.isSelected
    ).length > 0
  );
};

export const remainingMemberships = (
  itemsInCart: CartItem[],
  numberOfCheckedInParticipants: number
) => {
  const membershipsCountInCart =
    itemsInCart?.reduce(
      (acc: number, cartItem: CartItem) =>
        isMembership(cartItem) ? acc + cartItem.quantity : acc,
      0
    ) || 0;

  return numberOfCheckedInParticipants - membershipsCountInCart;
};

export const isBankPaymentEligible = (items: CartItem[] | undefined) => {
  if (!items) return false;

  const productTypes = [...new Set(items.map(i => i.type))];

  //CC-1806 - only enable bank payments for membership and annual passes and any associated fees
  return productTypes.every(p =>
    p === PointOfSaleCategory.Membership ||
    p === PointOfSaleCategory.AnnualPass ||
    p === PointOfSaleCategory.Fee
  );
};
